"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { User, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import SettingsDropdown from "@/components/SettingsDropdown";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export function Header() {
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      toast.success("성공적으로 로그아웃되었습니다.");
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("로그아웃에 실패했습니다. 다시 시도해 주세요.");
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background shadow-xs">
      <div className="container mx-auto flex h-16 items-center justify-between gap-2 sm:gap-4 ">
        {/* Logo and Title */}
        <Link href="/" className="flex min-w-0 shrink items-center">
          <Image
            src="/images/moornmo_1755937816500-removebg-preview.png"
            alt="PriMES - 제조실행시스템"
            width={120}
            height={40}
            className="h-7 w-auto sm:h-8 md:h-10"
            priority
          />

          <div className="ml-1.5 flex flex-col sm:ml-2">
            <h1 className="text-lg font-bold text-brand-600 dark:text-white sm:text-xl md:text-2xl">
              PriMES
            </h1>
            <p className="hidden text-xs text-brand-600 dark:text-gray-400 sm:block sm:text-sm">
              제조실행시스템
            </p>
          </div>
        </Link>

        {/* POP Button */}
        <Button
          size="lg"
          className="h-9 shrink-0 rounded-full bg-brand-600 px-4 text-sm font-bold text-white hover:bg-brand-700 sm:h-10 sm:px-6 sm:text-base md:h-12 md:px-10 md:text-lg lg:px-12"
        >
          POP
        </Button>

        {/* Right: User info and settings */}
        <div className="flex min-w-0 shrink items-center gap-1 sm:gap-2">
          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1.5 text-left transition-colors hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 sm:gap-2 sm:p-2"
                data-testid="user-menu-trigger"
              >
                <User className="h-4 w-4 shrink-0 text-gray-600 dark:text-gray-300 sm:h-5 sm:w-5" />
                <span className="hidden whitespace-nowrap text-xs font-medium text-gray-600 dark:text-gray-300 sm:inline sm:text-sm">
                  관리자
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand-600" />
                관리자 메뉴
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="gap-2 text-red-600 focus:text-red-600"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <>
                    <Spinner className="h-4 w-4 text-red-600" />
                    로그아웃 중...
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4" />
                    로그아웃
                  </>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Dynamic Settings Dropdown - Client Component */}
          <SettingsDropdown />
        </div>
      </div>
    </header>
  );
}
